/**
 * Cascadia multiplayer game store.
 *
 * Implements the `useSyncExternalStore`-compatible pattern following the
 * house pattern from yinsh/multiplayer-game-store.ts.
 *
 * Key behaviours (per multiplayer-e2e-guide.md):
 *   1. initializeAsHost: fetches existing events first; replays if present
 *      (prevents "host page reload destroys in-progress game" bug class).
 *   2. applyRemoteEvent: deduplicates via lastAppliedSeq (prevents "WS echo
 *      double-applies optimistic updates" bug class).
 *   3. submitAction: optimistic apply with rollback on HTTP error.
 *   4. replayEvents: rebuilds state from the event log (called on WS reconnect).
 */

import type {
  Action,
  GameState,
  MarketSlotIndex,
  ValidationResult,
} from "@cloudnest/cascadia-engine";
import { applyAction, initGame } from "@cloudnest/cascadia-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveCascadiaGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("cascadia", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveCascadiaGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("cascadia", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface CascadiaMultiplayerState {
  readonly gameState: GameState | null;
  readonly history: readonly Action[];
  readonly lastError: ValidationResult | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
}

type ConnectionStatus = CascadiaMultiplayerState["connection"];

const INITIAL_STATE: CascadiaMultiplayerState = {
  gameState: null,
  history: [],
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
};

// ---------------------------------------------------------------------------
// Module-level singleton (mirrors yinsh pattern)
// ---------------------------------------------------------------------------

let state: CascadiaMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

// ---------------------------------------------------------------------------
// Event replay helper
// ---------------------------------------------------------------------------

/**
 * Replay a sequence of server events onto a fresh null state.
 * Returns the final GameState (or null if no "init" event was found).
 */
function replayEventList(
  events: readonly {
    eventType: string;
    payload: Record<string, unknown>;
    sequenceNumber: number;
  }[],
): {
  gameState: GameState | null;
  lastSeq: number;
  history: readonly Action[];
} {
  let gameState: GameState | null = null;
  let lastSeq = 0;
  const history: Action[] = [];

  for (const event of events) {
    const payload = event.payload as Record<string, unknown>;

    if (event.eventType === "init") {
      gameState = payload.state as unknown as GameState;
    } else if (gameState) {
      // Reconstruct action from event type and payload
      const action = eventPayloadToAction(event.eventType, payload);
      if (action) {
        const result = applyAction(gameState, action);
        if (result.ok) {
          gameState = result.state;
          history.push(action);
        }
      }
    }
    lastSeq = event.sequenceNumber;
  }

  return { gameState, lastSeq, history };
}

/**
 * Convert a server event type + payload back to an Action for replay.
 * Returns null for event types that are server-only (init, refill, etc.).
 */
function eventPayloadToAction(
  eventType: string,
  payload: Record<string, unknown>,
): Action | null {
  const version = (payload.stateVersion as number | undefined) ?? 0;
  switch (eventType) {
    case "DraftPair":
      return {
        type: "DraftPair",
        slot: payload.slot as MarketSlotIndex,
        stateVersion: version,
      };
    case "DraftDecoupled":
      return {
        type: "DraftDecoupled",
        tileSlot: payload.tileSlot as MarketSlotIndex,
        wildlifeSlot: payload.wildlifeSlot as MarketSlotIndex,
        stateVersion: version,
      };
    case "SpendNatureDecouple":
      return { type: "SpendNatureDecouple", stateVersion: version };
    case "SpendNatureWipe":
      return {
        type: "SpendNatureWipe",
        slots: payload.slots as readonly MarketSlotIndex[],
        stateVersion: version,
      };
    case "WipeMarketOptional":
      return { type: "WipeMarketOptional", stateVersion: version };
    case "PlaceTile":
      return {
        type: "PlaceTile",
        coord: payload.coord as { q: number; r: number },
        // Carry the placed orientation across the relay; replay-safe
        // default 0 for pre-rotation events (§583).
        rotation: (payload.rotation as 0 | 1 | 2 | 3 | 4 | 5 | undefined) ?? 0,
        stateVersion: version,
      };
    case "PlaceWildlife":
      return {
        type: "PlaceWildlife",
        coord: payload.coord as { q: number; r: number },
        stateVersion: version,
      };
    case "ReturnWildlife":
      return { type: "ReturnWildlife", stateVersion: version };
    case "UnlockLandmark":
      return {
        type: "UnlockLandmark",
        habitat: payload.habitat as Action extends { type: "UnlockLandmark" }
          ? typeof payload.habitat
          : never,
        stateVersion: version,
      } as unknown as Action;
    case "ChooseLandmarkCard":
      return {
        type: "ChooseLandmarkCard",
        cardId: payload.cardId as string,
        stateVersion: version,
      };
    case "SkipLandmark":
      return { type: "SkipLandmark", stateVersion: version };
    case "EndTurn":
      return { type: "EndTurn", stateVersion: version };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Actions (exported object, same pattern as yinsh)
// ---------------------------------------------------------------------------

export const cascadiaMultiplayerActions = {
  /**
   * Called on host mount.
   *
   * Bug class prevented: "Host page reload destroys in-progress game".
   * If existing events are found, replay them instead of calling initGame again.
   */
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    playerNames: readonly string[] = [],
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      connection: "connecting",
    };
    notify();

    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = eventsResponse.success
      ? (eventsResponse.data ?? [])
      : [];

    if (existingEvents.length > 0) {
      // Reconnect path: replay from the event log
      const { gameState, lastSeq, history } = replayEventList(existingEvents);
      state = {
        ...state,
        gameState,
        lastAppliedSeq: lastSeq,
        history,
        connection: "open",
      };
      notify();
      return;
    }

    // New game: create initial state and persist the init event
    // hostPlayerId must be a seated player — use playerIds[0] (room creator)
    const hostPlayerId = playerIds[0] ?? myPlayerId;
    const playerSeating = Array.from(playerIds).map((id, i) => ({
      playerId: id,
      displayName: playerNames[i]?.trim() || id || `Player ${i + 1}`,
    }));

    const initResult = initGame({
      gameId: roomId,
      seed: roomId,
      playerSeating,
      scoringCards: {
        bear: "bear_A",
        elk: "elk_A",
        salmon: "salmon_A",
        hawk: "hawk_A",
        fox: "fox_A",
      },
      modulesOn: [],
      hostPlayerId,
      createdAt: new Date().toISOString(),
    });

    if (!initResult.ok) {
      state = {
        ...state,
        lastError: { valid: false, error: initResult.error, details: {} },
        connection: "closed",
      };
      notify();
      return;
    }

    const gameState = initResult.value;
    state = { ...state, gameState, connection: "open" };
    notify();

    try {
      const response = await getGameApi().submitEvent(roomId, "init", {
        state: gameState as unknown as Record<string, unknown>,
      });
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      state = {
        ...state,
        lastError: {
          valid: false,
          error:
            error instanceof Error
              ? error.message
              : "게임 초기화에 실패했습니다",
          details: {},
        },
      };
      notify();
    }
  },

  /**
   * Called on guest mount. Fetches existing events and replays them.
   */
  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      connection: "connecting",
    };
    notify();

    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data || response.data.length === 0) {
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      const { gameState, lastSeq, history } = replayEventList(response.data);
      if (gameState && state.roomId === roomId) {
        state = {
          ...state,
          gameState,
          lastAppliedSeq: lastSeq,
          history,
          connection: "open",
        };
        notify();
      }
    } catch {
      // Non-fatal — WS reconnect will trigger a full replay
      state = { ...state, connection: "open" };
      notify();
    }
  },

  /**
   * Fetches all events and rebuilds state from scratch.
   * Called on WS reconnect to catch missed events.
   */
  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;

      const { gameState, lastSeq, history } = replayEventList(response.data);
      state = { ...state, gameState, lastAppliedSeq: lastSeq, history };
      notify();
    } catch (error) {
      state = {
        ...state,
        lastError: {
          valid: false,
          error:
            error instanceof Error
              ? error.message
              : "이벤트를 불러올 수 없습니다",
          details: {},
        },
      };
      notify();
    }
  },

  /**
   * Applies a remote WS event. Deduplicates via lastAppliedSeq.
   *
   * Bug class prevented: "WS echo double-applies optimistic updates".
   * If seq <= lastAppliedSeq, this event was already applied (optimistically or
   * via a previous echo). Skip the apply but do NOT roll back.
   */
  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    if (!state.gameState && eventType !== "init") return;

    if (eventType === "init") {
      const nextState = payload.state as unknown as GameState;
      state = { ...state, gameState: nextState, lastAppliedSeq: seq };
      notify();
      return;
    }

    // Check if optimistic update already applied (dedup for matching action echo)
    const action = eventPayloadToAction(eventType, payload);
    if (!action || !state.gameState) {
      state = { ...state, lastAppliedSeq: seq };
      notify();
      return;
    }

    const result = applyAction(state.gameState, action);
    if (result.ok) {
      state = {
        ...state,
        gameState: result.state,
        lastAppliedSeq: seq,
        history: [...state.history, action],
      };
    } else {
      // Server accepted it but we failed locally — bump seq anyway to stay in sync
      state = { ...state, lastAppliedSeq: seq };
    }
    notify();
  },

  /**
   * Submit an action with optimistic apply and rollback on failure.
   */
  submitAction: async (
    action: Action,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId) {
      return { success: false, error: "No game in progress" };
    }

    // Validate locally first
    const result = applyAction(state.gameState, action);
    if (!result.ok) {
      state = { ...state, lastError: result.validation };
      notify();
      return { success: false, error: result.validation.error };
    }

    // Optimistic apply
    const prevGameState = state.gameState;
    const prevHistory = state.history;
    state = {
      ...state,
      gameState: result.state,
      lastError: null,
      history: [...state.history, action],
    };
    notify();

    const roomId = state.roomId ?? "";
    const eventType = actionTypeToEventType(action.type);
    const eventPayload = actionToEventPayload(action);

    try {
      const response = await getGameApi().submitEvent(
        roomId,
        eventType,
        eventPayload,
      );
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
      return { success: true };
    } catch (error) {
      // Rollback on failure
      state = {
        ...state,
        gameState: prevGameState,
        history: prevHistory,
        lastError: {
          valid: false,
          error: error instanceof Error ? error.message : "제출 실패",
          details: {},
        },
      };
      notify();
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출 실패",
      };
    }
  },

  setConnection: (status: ConnectionStatus): void => {
    state = { ...state, connection: status };
    notify();
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function actionTypeToEventType(type: Action["type"]): string {
  return type;
}

function actionToEventPayload(action: Action): Record<string, unknown> {
  const base = {
    stateVersion: (action as { stateVersion?: number }).stateVersion,
  };
  switch (action.type) {
    case "DraftPair":
      return { ...base, slot: action.slot };
    case "DraftDecoupled":
      return {
        ...base,
        tileSlot: action.tileSlot,
        wildlifeSlot: action.wildlifeSlot,
      };
    case "SpendNatureDecouple":
      return base;
    case "SpendNatureWipe":
      return { ...base, slots: action.slots };
    case "WipeMarketOptional":
      return base;
    case "PlaceTile":
      return { ...base, coord: action.coord, rotation: action.rotation ?? 0 };
    case "PlaceWildlife":
      return { ...base, coord: action.coord };
    case "ReturnWildlife":
      return base;
    case "UnlockLandmark":
      return { ...base, habitat: action.habitat };
    case "ChooseLandmarkCard":
      return { ...base, cardId: action.cardId };
    case "SkipLandmark":
      return base;
    case "EndTurn":
      return base;
    case "Init":
      return base;
    default: {
      const _exhaustive: never = action;
      return { type: String((_exhaustive as Action).type) };
    }
  }
}

// ---------------------------------------------------------------------------
// Store interface + React hook
// ---------------------------------------------------------------------------

export const cascadiaMultiplayerStore = {
  getSnapshot: (): CascadiaMultiplayerState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useCascadiaMultiplayerStore(): CascadiaMultiplayerState {
  return useSyncExternalStore(
    cascadiaMultiplayerStore.subscribe,
    cascadiaMultiplayerStore.getSnapshot,
  );
}
